import { packFrameGrid } from "@/lib/frame-grid-packing";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import type { Node } from "@xyflow/react";
import { getCanvasTimePages } from "@/lib/canvas-time-page-membership";
import type { FrameData } from "@/lib/frame-data";
import { formatDate, translate } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { isWorkspaceFrame } from "@/lib/frame-classification";
import {
  CANVAS_TIME_PAGE_FULL_WIDTH_COLUMNS,
  CANVAS_TIME_PAGE_MAX_CONTENT_HEIGHT,
  CANVAS_TIME_PAGE_ROWS,
} from "@/lib/canvas-time-page-grid";

export const FULL_WIDTH_TIME_PAGE_SIZE =
  CANVAS_TIME_PAGE_FULL_WIDTH_COLUMNS * CANVAS_TIME_PAGE_ROWS;

export interface CanvasTimePage {
  id: string;
  /** Translated when the pages are built; the first page reads "Recent". */
  label: string;
  nodeIds: string[];
  frameCount: number;
}

function isPinned(node: Node<FrameData>): boolean {
  return node.data.metadata?.pinned === true;
}

function createdDescending(
  left: Node<FrameData>,
  right: Node<FrameData>,
): number {
  return (
    right.data.createdAt.localeCompare(left.data.createdAt) ||
    left.id.localeCompare(right.id)
  );
}

function pageDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime()))
    return translate(canvasMessages, "timePageOlder");
  return formatDate(date, { month: "short", day: "numeric", timeZone: "UTC" });
}

function rangeLabel(nodes: Node<FrameData>[]): string {
  const newest = nodes[0];
  const oldest = nodes[nodes.length - 1];
  if (!newest || !oldest) return translate(canvasMessages, "timePageOlder");
  const newestLabel = pageDate(newest.data.createdAt);
  const oldestLabel = pageDate(oldest.data.createdAt);
  return newestLabel === oldestLabel
    ? newestLabel
    : `${oldestLabel}–${newestLabel}`;
}

function fittingPrefix(
  nodes: Node<FrameData>[],
  columns: number,
  preceding: Node<FrameData>[] = [],
): number {
  const frames = [...preceding, ...nodes].map((node) => {
    const size = frameNodeSize(node);
    return { id: node.id, w: size.width, h: size.height };
  });
  let accepted = 0;
  for (let count = 1; count <= nodes.length; count++) {
    const packed = packFrameGrid(
      frames.slice(0, preceding.length + count),
      columns,
    );
    if (
      (accepted > 0 || preceding.length > 0) &&
      packed.some(({ y, h }) => y + h > CANVAS_TIME_PAGE_MAX_CONTENT_HEIGHT)
    )
      break;
    accepted = count;
  }
  return accepted;
}

export function buildCanvasTimePages(
  nodes: Node<FrameData>[],
  currentSpaceId?: string | null,
): CanvasTimePage[] {
  const visible = nodes.filter(
    (node) =>
      isWorkspaceFrame(node.data.frameType) &&
      !node.hidden &&
      node.data.metadata?.hidden !== true &&
      (!currentSpaceId || node.data.spaceId === currentSpaceId),
  );
  if (visible.length === 0) return [];

  const pinned: Node<FrameData>[] = [];
  const chronological: Node<FrameData>[] = [];
  for (const node of visible) {
    if (isPinned(node)) {
      pinned.push(node);
    } else {
      chronological.push(node);
    }
  }

  pinned.sort(createdDescending);
  chronological.sort(createdDescending);
  const recentLimit = fittingPrefix(
    chronological,
    CANVAS_TIME_PAGE_FULL_WIDTH_COLUMNS,
    pinned,
  );
  const recent = chronological.slice(0, recentLimit);
  const pages: CanvasTimePage[] = [
    {
      id: "recent",
      label: translate(canvasMessages, "timePageRecent"),
      nodeIds: [...pinned, ...recent].map((node) => node.id),
      frameCount: pinned.length + recent.length,
    },
  ];

  const older = chronological.slice(recentLimit);
  for (let start = 0; start < older.length; ) {
    const count = fittingPrefix(
      older.slice(start),
      CANVAS_TIME_PAGE_FULL_WIDTH_COLUMNS,
    );
    const pageNodes = older.slice(start, start + count);
    start += count;
    pages.push({
      id: `time-${pageNodes[0]?.id ?? start}-${pageNodes[pageNodes.length - 1]?.id ?? start}`,
      label: rangeLabel(pageNodes),
      nodeIds: pageNodes.map((node) => node.id),
      frameCount: pageNodes.length,
    });
  }

  return pages.filter((page) => page.nodeIds.length > 0);
}

export function resolveCanvasTimePage(
  nodes: Node<FrameData>[],
  requestedIndex: number,
  currentSpaceId?: string | null,
): { page: CanvasTimePage | null; index: number; pages: CanvasTimePage[] } {
  const pages = getCanvasTimePages(nodes, currentSpaceId);
  if (pages.length === 0) return { page: null, index: 0, pages };
  const index = Math.min(Math.max(0, requestedIndex), pages.length - 1);
  return { page: pages[index] ?? null, index, pages };
}

export function findCanvasTimePageIndex(
  nodes: Node<FrameData>[],
  frameId: string,
  currentSpaceId?: string | null,
): number | null {
  const index = getCanvasTimePages(nodes, currentSpaceId).findIndex((page) =>
    page.nodeIds.includes(frameId),
  );
  return index >= 0 ? index : null;
}
